import React from 'react';

interface SkeletonLoaderProps {
  variant?: 'card' | 'text' | 'epic' | 'timeline' | 'chart';
  count?: number;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ 
  variant = 'card', 
  count = 1 
}) => {
  const renderSkeleton = () => {
    switch (variant) {
      case 'epic':
        return (
          <div className="glass apple-card-glass rounded-apple-xl p-6 animate-pulse">
            <div className="flex items-start justify-between mb-4">
              <div className="space-y-2 flex-1">
                <div className="h-6 bg-gray-300/30 rounded-apple w-2/3"></div>
                <div className="h-4 bg-gray-300/20 rounded-apple w-1/2"></div>
              </div>
              <div className="h-8 w-8 bg-gray-300/30 rounded-full"></div>
            </div>
            <div className="space-y-2 mb-4">
              <div className="h-3 bg-gray-300/20 rounded-apple w-full"></div>
              <div className="h-3 bg-gray-300/20 rounded-apple w-4/5"></div>
            </div>
            <div className="flex gap-2">
              <div className="h-6 bg-gray-300/20 rounded-apple-lg w-16"></div>
              <div className="h-6 bg-gray-300/20 rounded-apple-lg w-20"></div>
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="space-y-6">
            {/* Header skeleton */}
            <div className="flex items-center justify-between p-4 glass rounded-apple-xl animate-pulse">
              <div className="h-8 bg-gray-300/30 rounded-apple w-48"></div>
              <div className="flex gap-2">
                <div className="h-10 bg-gray-300/30 rounded-apple w-24"></div>
                <div className="h-10 bg-gray-300/30 rounded-apple w-24"></div>
              </div>
            </div>
            
            {/* Timeline content skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="glass apple-card-glass rounded-apple-xl p-4 animate-pulse">
                  <div className="h-4 bg-gray-300/30 rounded-apple w-1/2 mb-3"></div>
                  <div className="space-y-2">
                    <div className="h-20 bg-gray-300/20 rounded-apple"></div>
                    <div className="h-3 bg-gray-300/20 rounded-apple w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'chart':
        return (
          <div className="glass apple-card-glass rounded-apple-xl p-6 animate-pulse">
            <div className="h-6 bg-gray-300/30 rounded-apple w-1/3 mb-6"></div>
            <div className="space-y-4">
              <div className="flex items-end justify-between h-48 gap-2">
                {[...Array(7)].map((_, i) => (
                  <div 
                    key={i} 
                    className="flex-1 bg-gray-300/20 rounded-t-apple"
                    style={{ height: `${Math.random() * 100 + 50}px` }}
                  ></div>
                ))}
              </div>
              <div className="flex justify-between">
                {[...Array(7)].map((_, i) => (
                  <div key={i} className="h-3 bg-gray-300/20 rounded-apple w-8"></div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-2 animate-pulse">
            <div className="h-4 bg-gray-300/30 rounded-apple w-full"></div>
            <div className="h-4 bg-gray-300/20 rounded-apple w-5/6"></div>
            <div className="h-4 bg-gray-300/20 rounded-apple w-4/6"></div>
          </div>
        );

      case 'card':
      default:
        return (
          <div className="glass apple-card-glass rounded-apple-xl p-6 animate-pulse">
            <div className="h-6 bg-gray-300/30 rounded-apple w-2/3 mb-4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-300/20 rounded-apple w-full"></div>
              <div className="h-4 bg-gray-300/20 rounded-apple w-5/6"></div>
              <div className="h-4 bg-gray-300/20 rounded-apple w-4/6"></div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {[...Array(count)].map((_, index) => (
        <div key={index}>{renderSkeleton()}</div>
      ))}
    </div>
  );
};
